/**
 * The options `main.tsx` hands to `Sentry.init`.
 *
 * They live here, not in `main.tsx`, for one reason: `main.tsx` also starts the app
 * (`createRoot`) and imports `App`, which reaches the Supabase client. Importing that from
 * a test throws `supabaseUrl is required` and then crashes on a missing root element, so
 * the rules below could never be tested where they were written. Here they can be imported
 * on their own, and `src/__tests__/sentry-scrub.test.ts` checks each one.
 *
 * Requirements: FR-011 (the recovery token never reaches third-party monitoring).
 */

import * as Sentry from "@sentry/react";
import { scrubBreadcrumbUrls, scrubEventUrl } from "@/lib/sentry-scrub";
import { BOOT_HASH, RESET_PATH } from "@/services/auth/recovery";

/**
 * Should session replay stay off for a page load at this address?
 *
 * Plain words: replay records the page itself, including the address bar URL, and it sends
 * that recording as its own package. The filters below can clean crash reports and speed
 * measurements, but nothing can reach inside a replay. So replay stays off whenever the
 * address could be carrying a live token (FR-011, D-046):
 *
 *  - When the fragment carries one, whatever the page. A reset email's link can be pointed at
 *    any page on the site: the redirect allow-list permits every path, and `redirect_to` is a
 *    request parameter anyone can set. So the page alone can never decide it.
 *  - On the reset page, however the address spells it. The router ignores case and trailing
 *    slashes and decodes %-escapes, so "/Reset-Password/" renders the same page, and an exact
 *    string compare let it through.
 */
export function shouldSkipReplay(pathname: string, hash: string): boolean {
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  if (fragment.has("access_token") || fragment.has("refresh_token")) return true;

  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // A malformed %-escape. The router cannot decode it either, and matches the raw path,
    // which cannot equal the reset route (that has no "%" in it).
    return false;
  }
  return decoded.toLowerCase().replace(/\/+$/, "") === RESET_PATH;
}

/**
 * Decided once, at module evaluation, which is the same moment Sentry starts. Later the app
 * may navigate away, but session replay is either started at boot or not at all. The
 * fragment is read from `BOOT_HASH`, the snapshot taken before supabase-js could clear it.
 */
const skipReplay =
  typeof window !== "undefined" && shouldSkipReplay(window.location.pathname, BOOT_HASH);

/** Every other page load still gets replay (FR-011). */
const integrations = [
  Sentry.browserTracingIntegration(),
  ...(skipReplay
    ? []
    : [
        Sentry.replayIntegration({
          maskAllText: true, // mask all text in replays (financial data privacy)
          blockAllMedia: false,
        }),
      ]),
];

export const sentryOptions: Sentry.BrowserOptions = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN, // silently no-ops locally without a DSN
  integrations,
  tracesSampleRate: 0.2, // 20% of sessions get performance traces
  replaysSessionSampleRate: 0.1, // 10% of normal sessions recorded
  replaysOnErrorSampleRate: 1.0, // 100% of crash sessions recorded, the key feature

  // Three separate paths out, and each one needs its own filter.
  // `beforeSend` sees error events only. Sampled performance transactions go out through
  // `beforeSendTransaction`, and the trail of navigation breadcrumbs through
  // `beforeBreadcrumb`. Missing any one of them would still ship the token.
  beforeSend(event) {
    scrubEventUrl(event);
    return event;
  },
  beforeSendTransaction(event) {
    scrubEventUrl(event);
    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    scrubBreadcrumbUrls(breadcrumb);
    return breadcrumb;
  },
};
